import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">漫画未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: comic.title,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: '2024-01-01',
          dateModified: comic.updatedAt,
          image: comic.cover,
          publisher: { '@type': 'Organization', name: '178漫画网' },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sdidsa.cn' },
            ...(category ? [{ '@type': 'ListItem', position: 2, name: `${category.name}漫画`, item: `https://sdidsa.cn/category/${category.slug}` }] : []),
            { '@type': 'ListItem', position: category ? 3 : 2, name: comic.title, item: `https://sdidsa.cn/comic/${comic.id}` },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100">
            <div className="w-full px-4 md:px-6 py-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  ...(category ? [{ label: `${category.name}漫画`, to: `/category/${category.slug}` }] : []),
                  { label: comic.title },
                ]}
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-56 aspect-[5/7] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">
                    <i className="ri-user-line mr-1"></i> {comic.author}
                  </span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-star-fill text-accent-500 mr-1"></i> {comic.rating} 分
                  </span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-eye-line mr-1"></i> {formatViews(comic.views)} 阅读
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/comic/${comic.id}/read/1`}
                    className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    开始阅读 第1话
                  </Link>
                  <Link
                    to={`/comic/${comic.id}/read/${comic.chapters[0].num}`}
                    className="px-6 py-2.5 rounded-lg bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-colors border border-background-200 cursor-pointer whitespace-nowrap"
                  >
                    阅读最新 第{comic.chapters[0].num}话
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 pb-16">
            <div className="mb-6">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-1">章节列表</h2>
              <p className="text-sm text-foreground-500">共 {comic.chapters.length}+ 话</p>
            </div>

            <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="divide-y divide-background-200/70">
                {comic.chapters.map((chapter) => (
                  <Link
                    key={chapter.num}
                    to={`/comic/${comic.id}/read/${chapter.num}`}
                    className="flex items-center justify-between p-4 hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">
                        第{chapter.num}话
                      </span>
                      <span className="text-sm text-foreground-600 truncate">
                        {chapter.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-4">
                      {chapter.updatedAt}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}